/**
 * GraphSummaryPanel — the AI graph summary as its own inspector view.
 *
 * The summary describes the whole canvas, not a selected wallet, so it does not
 * belong in the node-scoped Analysis tab: reaching it there meant selecting a
 * node, opening a tab, expanding an accordion, and scrolling — four steps a
 * first-time user has no reason to guess. This panel takes the inspector slot
 * directly when the canvas tray button runs a summary, and hands the slot back
 * via "Back to node" when the user is done reading.
 *
 * It owns no data: the summary text, loading flag, and meta all live in
 * usePanelStore so the result survives closing the panel, switching nodes, and
 * reopening from the tray.
 */
import React from 'react';
import { Loader2, RefreshCw, ScanLine, X, ArrowLeft } from 'lucide-react';
import { clsx } from 'clsx';
import { IntelligenceNoteView } from '../ui/IntelligenceNoteView';
import { InfoHint } from '../InfoHint';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import type { GraphSummaryMeta } from '../../stores/usePanelStore';

export interface GraphSummaryPanelProps {
    summary: string;
    loading: boolean;
    /** Canvas shape when the summary was generated (null before first run). */
    meta: GraphSummaryMeta | null;
    /** Live canvas counts, for the stale hint. */
    nodeCount: number;
    edgeCount: number;
    onRegenerate: () => void;
    onClose: () => void;
    /** Present only when a wallet is selected, so there is a node to go back to. */
    onBackToNode?: () => void;
}

function formatWhen(ts: number): string {
    const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    return `${hours} hr ago`;
}

export const GraphSummaryPanel: React.FC<GraphSummaryPanelProps> = ({
    summary,
    loading,
    meta,
    nodeCount,
    edgeCount,
    onRegenerate,
    onClose,
    onBackToNode,
}) => {
    // Same storage key as the Node Inspector: the panels share the slot, so a
    // width the officer picked for one should not jump when the other appears.
    const { width, isResizing, handleMouseDown } = useResizablePanel({
        storageKey: 'workbench-inspector-width-v5',
        minWidth: 440,
        maxWidth: 780,
        defaultWidth: 540,
        compactBreakpoint: 1536,
    });

    const hasSummary = Boolean(summary?.trim());
    const isStale = Boolean(
        meta && !loading && (meta.nodeCount !== nodeCount || meta.edgeCount !== edgeCount),
    );

    return (
        <div
            className={clsx(
                'relative bg-card border-l border-border z-10 flex flex-col overflow-hidden',
                !isResizing && 'transition-[width] duration-200 ease-in-out',
                isResizing && 'opacity-90 select-none',
            )}
            style={{ width, maxHeight: '100%' }}
            data-testid="wb-graph-summary-panel"
            data-tour="graph-summary-panel"
        >
            <div
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/50 transition-colors z-20"
                onMouseDown={handleMouseDown}
                title="Drag to resize panel"
            />

            <div className="p-4 flex flex-col flex-1 overflow-hidden min-w-[400px]">
                <div className="flex items-center justify-between mb-3 shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <ScanLine className="w-4 h-4 text-primary shrink-0" aria-hidden />
                        <h3 className="font-semibold text-lg leading-tight truncate">Graph Summary</h3>
                        <InfoHint
                            label="What is the Graph Summary?"
                            text="A plain-language read of everything currently on the canvas — key wallets, how funds move between them, and what looks risky. It covers the whole graph, not the wallet you selected, so it is the fastest way to understand an unfamiliar case."
                        />
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                        {onBackToNode && (
                            <button
                                onClick={onBackToNode}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-primary/10 hover:text-primary rounded transition-colors border border-border/50 bg-secondary/50 text-xs font-medium"
                                title="Return to the Node Inspector for the selected wallet"
                                data-testid="wb-graph-summary-back"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                <span>Back to node</span>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title="Close panel"
                            aria-label="Close graph summary"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
                    <span className="text-[11px] text-muted-foreground truncate">
                        {nodeCount} address{nodeCount === 1 ? '' : 'es'} · {edgeCount} transfer{edgeCount === 1 ? '' : 's'}
                        {meta && !loading ? ` · generated ${formatWhen(meta.generatedAt)}` : ''}
                    </span>
                    <button
                        onClick={onRegenerate}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-2 py-1 shrink-0 rounded text-[11px] font-medium border border-border/60 bg-secondary/50 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                        title="Run the summary again on the current canvas"
                        data-testid="wb-graph-summary-regenerate"
                    >
                        <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
                        {hasSummary ? 'Regenerate' : 'Generate'}
                    </button>
                </div>

                {isStale && (
                    <div
                        className="mb-2 px-2.5 py-1.5 shrink-0 rounded-md border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-700 dark:text-amber-200"
                        data-testid="wb-graph-summary-stale"
                    >
                        The canvas changed since this summary ran ({meta!.nodeCount} → {nodeCount} addresses).
                        Regenerate for an up-to-date read.
                    </div>
                )}

                <div className="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden styled-scrollbar page-scrollbar overscroll-y-contain pb-4">
                    {loading && (
                        <div
                            className="flex items-center gap-2 px-3 py-3 rounded-lg border border-primary/20 bg-primary/5 text-xs text-muted-foreground"
                            data-testid="wb-graph-summary-loading"
                        >
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                            Reading the graph and writing the summary…
                        </div>
                    )}

                    {!loading && hasSummary && (
                        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-3">
                            <IntelligenceNoteView
                                markdown={summary}
                                eyebrow="Whole canvas"
                                maxHeightClass="max-h-none"
                            />
                        </div>
                    )}

                    {!loading && !hasSummary && (
                        <div
                            className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-3 py-3 text-[11px] leading-relaxed text-muted-foreground"
                            data-testid="wb-graph-summary-empty"
                        >
                            {nodeCount === 0
                                ? 'The canvas is empty. Add an address from the Omnibar, then generate a summary.'
                                : 'No summary yet for this canvas. Press Generate to read the graph.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
